import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Settings as SettingsIcon, Building, Users, Tag, Save, FileText, Plus, Trash2, ShieldCheck, Shield, Edit } from 'lucide-react';
import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';
import RazorpayModal from '../../components/RazorpayModal';
import PayPalModal from '../../components/PayPalModal';

const Settings = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('company');

  const tabs = [
    { id: 'company', name: 'Company Settings', icon: Building },
    { id: 'branches', name: 'Branch Management', icon: Building },
    { id: 'technicians', name: 'Technicians', icon: Users },
    { id: 'roles', name: 'Role Settings', icon: ShieldCheck },
    { id: 'users', name: 'User Management', icon: Users },
    { id: 'categories', name: 'Categories', icon: Tag },
    { id: 'terms', name: 'Terms & Conditions', icon: FileText },
    { id: 'receipt', name: 'Receipt Settings', icon: FileText }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <SettingsIcon className="w-7 h-7 mr-3 text-blue-600" />
          Settings
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure system settings and preferences
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto scrollbar-none">
        <nav className="-mb-px flex space-x-6 min-w-max pb-1 px-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-5 h-5 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'company' && <CompanySettings />}
        {activeTab === 'branches' && <BranchManagement />}
        {activeTab === 'technicians' && <TechnicianManagement />}
        {activeTab === 'roles' && <RoleManagement />}
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'categories' && <CategoryManagement />}
        {activeTab === 'terms' && <TermsManagement />}
        {activeTab === 'receipt' && <ReceiptSettings />}
      </div>
    </div>
  );
};

// Company Settings Component
const CompanySettings = () => {
  const queryClient = useQueryClient();
  const { checkAuth } = useAuth();
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    website: '',
    stdCode: '',
    gstNumber: '',
    jobIdPrefix: 'JOB',
    jobIdDigits: 3,
    googleDriveLink: '',
    currencySymbol: '₹',
    currencyCode: 'INR'
  });

  const { isLoading } = useQuery('company-settings', async () => {
    const response = await api.get('/api/settings/company');
    return response.data.data;
  }, {
    onSuccess: (data) => {
      if (data) {
        // Sanitize data to avoid null values in controlled inputs
        const sanitized = { ...data };
        Object.keys(sanitized).forEach(key => {
          if (sanitized[key] === null) {
            sanitized[key] = key === 'jobIdDigits' ? 3 : '';
          }
        });
        setFormData(sanitized);
      }
    }
  });

  const updateMutation = useMutation(
    async (data) => {
      const response = await api.put('/api/settings/company', data);
      return response.data;
    },
    {
      onSuccess: async () => {
        toast.success('Company settings updated successfully!');
        queryClient.invalidateQueries('company-settings');
        await checkAuth();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('logo', file);

    const uploadText = formData.googleDriveConnected 
      ? 'Uploading logo to Google Drive...' 
      : 'Uploading logo to local server...';
      
    const uploadToastId = toast.loading(uploadText);
    try {
      const response = await api.post('/api/settings/company/logo', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success) {
        toast.success(formData.googleDriveConnected 
          ? 'Logo uploaded and saved to Google Drive!' 
          : 'Logo uploaded and saved to local server!', { id: uploadToastId });
        setFormData(prev => ({ ...prev, logo: response.data.logo }));
        queryClient.invalidateQueries('company-settings');
      } else {
        toast.error(response.data.message || 'Upload failed', { id: uploadToastId });
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload logo image', { id: uploadToastId });
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="spinner w-8 h-8"></div></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="card space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Logo Section */}
        <div className="md:col-span-2 flex flex-col sm:flex-row items-center gap-5 pb-6 border-b border-gray-100">
          <div className="relative group w-24 h-24 rounded-2xl overflow-hidden bg-slate-50 border-2 border-dashed border-gray-355 hover:border-blue-500 flex items-center justify-center transition-all">
            {formData.logo ? (
              <img
                src={formData.logo.startsWith('http') ? formData.logo : `${api.defaults.baseURL || ''}${formData.logo}`}
                alt="Company Logo"
                className="w-full h-full object-contain p-2"
                onError={(e) => {
                  e.target.src = "https://cdn-icons-png.flaticon.com/512/2942/2942544.png";
                }}
              />
            ) : (
              <div className="text-center p-2">
                <span className="text-2xl">🏢</span>
              </div>
            )}
          </div>
          <div className="space-y-2 text-center sm:text-left">
            <h4 className="text-sm font-bold text-gray-900 font-sans">Company Logo</h4>
            <p className="text-xs text-gray-500 max-w-md">
              {formData.googleDriveConnected 
                ? "Upload your company logo. It will be automatically saved to your client-isolated Google Drive folder and displayed on bills."
                : "Upload your company logo. It will be saved locally on the server. (You can also connect Google Drive below to store it in the cloud.)"}
            </p>
            <div className="flex items-center justify-center sm:justify-start gap-3">
              <label className="px-4 py-2 rounded-lg text-xs font-bold transition duration-200 cursor-pointer bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200">
                <span>Upload Logo Image</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="form-label">Company Name *</label>
          <input
            type="text"
            className="input-field"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="form-label">Address</label>
          <textarea
            className="input-field"
            rows="3"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label">City</label>
          <input
            type="text"
            className="input-field"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label">State</label>
          <input
            type="text"
            className="input-field"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label">Pincode</label>
          <input
            type="text"
            className="input-field"
            value={formData.pincode}
            onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label">Phone</label>
          <input
            type="tel"
            className="input-field"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label">Email</label>
          <input
            type="email"
            className="input-field"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label">Website</label>
          <input
            type="url"
            className="input-field"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label">STD Code (for WhatsApp)</label>
          <input
            type="text"
            className="input-field"
            value={formData.stdCode}
            onChange={(e) => setFormData({ ...formData, stdCode: e.target.value })}
            placeholder="+91"
          />
        </div>

        <div>
          <label className="form-label">GST Number</label>
          <input
            type="text"
            className="input-field"
            value={formData.gstNumber}
            onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value })}
          />
        </div>

        <div className="md:col-span-2 border-t border-gray-100 pt-6 mt-6">
          <div className="flex items-center space-x-2.5 mb-2">
            <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM19 18H6c-2.21 0-4-1.79-4-4 0-2.05 1.53-3.76 3.56-3.97l1.07-.11.5-.95C8.08 7.14 9.94 6 12 6c2.62 0 4.88 1.86 5.39 4.43l.3 1.5 1.53.11c1.56.1 2.78 1.41 2.78 2.96 0 1.65-1.35 3-3 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">
                Cloud Storage Settings (Google Drive)
              </h3>
              <p className="text-xs text-gray-500">
                Securely store job attachments (device photos, repair receipts) directly in your own Google Drive.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200/85 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-3">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-gray-700 text-sm">Connection Status:</span>
                {formData.googleDriveConnected ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                    Active & Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-150 text-gray-600 border border-gray-200">
                    Not Connected
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 max-w-md">
                {formData.googleDriveConnected 
                  ? "A secure connection is active. All job file attachments will automatically save to your personal cloud folder."
                  : "Link any Google account (personal or shop Gmail) to store uploads in Google Drive instead of the local server disk."}
              </p>
            </div>

            <div className="shrink-0">
              {formData.googleDriveConnected ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (window.confirm("Are you sure you want to disconnect your Google Drive? Attachments will fall back to local server storage.")) {
                      try {
                        toast.loading("Disconnecting Google Drive...", { id: "oauth-settings" });
                        const res = await api.post("/api/settings/google/disconnect");
                        if (res.data.success) {
                          toast.success("Google Drive disconnected successfully!", { id: "oauth-settings" });
                          queryClient.invalidateQueries("company-settings");
                        } else {
                          toast.error(res.data.message || "Failed to disconnect.", { id: "oauth-settings" });
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.message || "Failed to disconnect.", { id: "oauth-settings" });
                      }
                    }
                  }}
                  className="px-4 py-2 bg-red-50 text-red-650 hover:bg-red-100 hover:text-red-750 border border-red-200 rounded-lg text-xs font-bold transition duration-200"
                >
                  Disconnect Account
                </button>
              ) : (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      toast.loading("Opening secure Google login...", { id: "oauth-settings" });
                      const res = await api.get("/api/settings/google/auth-url");
                      toast.dismiss("oauth-settings");
                      if (res.data.authUrl) {
                        window.location.href = res.data.authUrl;
                      } else {
                        toast.error("Auth URL missing.");
                      }
                    } catch (err) {
                      toast.dismiss("oauth-settings");
                      toast.error(err.response?.data?.message || "Failed to start Google login.");
                    }
                  }}
                  className="px-4.5 py-2.2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition duration-200 flex items-center space-x-1.5 shadow-sm hover:shadow"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21.35 11.1H12v2.7h5.3c-.2 1.2-.9 2.1-2 2.8v2.3h3.2c1.9-1.8 3-4.4 3-7.8 0-.6-.1-1.2-.1-1.8z" fill="#fff"/>
                    <path d="M12 21c2.4 0 4.5-.8 6-2.2l-3.2-2.3c-.8.6-1.8.9-2.8.9-2.2 0-4-1.5-4.7-3.6H4v2.4c1.5 3 4.6 4.8 8 4.8z" fill="#fff"/>
                    <path d="M7.3 13.8c-.2-.6-.3-1.2-.3-1.8s.1-1.2.3-1.8V7.8H4c-.8 1.6-1.2 3.4-1.2 5.2s.4 3.6 1.2 5.2l3.3-2.6z" fill="#fff"/>
                    <path d="M12 6.8c1.3 0 2.5.5 3.4 1.3l2.6-2.6C16.5 4.2 14.4 3.4 12 3.4c-3.4 0-6.5 1.8-8 4.8l3.3 2.6c.7-2.1 2.5-3.6 4.7-3.6z" fill="#fff"/>
                  </svg>
                  <span>Connect Google Account</span>
                </button>
              )}
            </div>
          </div>

          {formData.googleDriveConnected && formData.googleDriveLink && (
            <div className="mt-3.5 bg-blue-50/70 border border-blue-100 rounded-xl p-4.5">
              <label className="block text-[10px] font-bold text-blue-800 uppercase tracking-wider mb-1.5">
                Active Storage Folder Link:
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  readOnly
                  className="w-full text-xs bg-white border border-blue-200 rounded-lg p-2 text-blue-700 font-mono focus:outline-none"
                  value={formData.googleDriveLink}
                />
                <a
                  href={formData.googleDriveLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition shrink-0"
                >
                  Open Folder
                </a>
              </div>
              <p className="text-[11px] text-blue-600 mt-2 font-medium flex items-center">
                <span className="mr-1">📂</span> Dynamic Root Folder: "Digital Repair Shop Uploads" automatically created inside your Google Drive.
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="form-label">Job ID Prefix</label>
          <input
            type="text"
            className="input-field"
            value={formData.jobIdPrefix}
            onChange={(e) => setFormData({ ...formData, jobIdPrefix: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label">Job ID Digits</label>
          <input
            type="number"
            className="input-field"
            value={formData.jobIdDigits}
            onChange={(e) => setFormData({ ...formData, jobIdDigits: parseInt(e.target.value) || 3 })}
            min="1"
            max="10"
          />
        </div>
      </div>

      <div className="md:col-span-2 border-t border-gray-100 pt-6 mt-2">
        <div className="flex items-center space-x-2.5 mb-2">
          <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
            <span className="text-xl font-bold">💵</span>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">
              System Currency Settings
            </h3>
            <p className="text-xs text-gray-500">
              Choose the default currency symbol and code to be displayed across all system pages (Dashboard, Jobs, Customers, Statement, etc.).
            </p>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200/85 grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <div>
            <label className="form-label">Select Currency</label>
            <select
              value={`${formData.currencyCode || 'INR'}|${formData.currencySymbol || '₹'}`}
              onChange={(e) => {
                const [code, symbol] = e.target.value.split('|');
                setFormData({ ...formData, currencyCode: code, currencySymbol: symbol });
              }}
              className="input-field cursor-pointer"
            >
              <option value="INR|₹">🇮🇳 Indian Rupee (INR - ₹)</option>
              <option value="USD|$">🇺🇸 US Dollar (USD - $)</option>
              <option value="EUR|€">🇪🇺 Euro (EUR - €)</option>
              <option value="GBP|£">🇬🇧 British Pound (GBP - £)</option>
              <option value="JPY|¥">🇯🇵 Japanese Yen (JPY - ¥)</option>
              <option value="AUD|A$">🇦🇺 Australian Dollar (AUD - A$)</option>
              <option value="CAD|C$">🇨🇦 Canadian Dollar (CAD - C$)</option>
              <option value="SGD|S$">🇸🇬 Singapore Dollar (SGD - S$)</option>
              <option value="AED|د.إ">🇦🇪 UAE Dirham (AED - د.إ)</option>
              <option value="SAR|ر.س">🇸🇦 Saudi Riyal (SAR - ر.س)</option>
              <option value="MYR|RM">🇲🇾 Malaysian Ringgit (MYR - RM)</option>
              <option value="ZAR|R">🇿🇦 South African Rand (ZAR - R)</option>
              <option value="NZD|NZ$">🇳🇿 New Zealand Dollar (NZD - NZ$)</option>
              <option value="KES|KSh">🇰🇪 Kenyan Shilling (KES - KSh)</option>
            </select>
          </div>
          <div className="flex items-center gap-3 bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm mt-5 sm:mt-0">
            <div className="text-3xl">💱</div>
            <div>
              <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Selected Active Format:</div>
              <div className="text-lg font-black text-blue-600 font-mono">
                {formData.currencySymbol || '₹'} 1,250.00 <span className="text-xs font-semibold text-slate-600">({formData.currencyCode || 'INR'})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={updateMutation.isLoading}
          className="btn-primary inline-flex items-center"
        >
          {updateMutation.isLoading ? (
            <>
              <div className="spinner mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Branch Management Component
const BranchManagement = () => {
  const queryClient = useQueryClient();
  const { user, checkAuth, currencySymbol } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [isRazorpayOpen, setIsRazorpayOpen] = useState(false);
  const [isPayPalOpen, setIsPayPalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' | 'paypal'
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    upiId: ''
  });

  const { data: branchesData, isLoading } = useQuery('branches', async () => {
    const response = await api.get('/api/branches');
    return response.data.data;
  });

  // planData is now provided directly by the backend via user.planData
  const clientPlan = React.useMemo(() => {
    if (!user) return null;
    return user.planData || null;
  }, [user]);

  const branches = branchesData || [];

  const baseLimit = (() => {
    if (!clientPlan) return 9999;
    const bl = clientPlan.branchLimit;
    if (!bl || bl === 'unlimited') return 9999;
    const parsed = parseInt(bl);
    return isNaN(parsed) ? 9999 : parsed;
  })();
  const extraBranches = user?.extraBranches || 0;
  const allowedBranchesCount = baseLimit + extraBranches;
  const isLimitReached = (user?.role === 'CLIENT' || user?.role === 'ADMIN') && baseLimit !== 9999 && branches.length >= allowedBranchesCount;

  const handlePurchaseSuccess = async () => {
    try {
      const res = await api.post(`/api/saas/clients/${user.clientId}/unlock-branch`);
      if (res.data.success) {
        toast.success('Successfully unlocked 1 additional branch location!');
        setIsRazorpayOpen(false);
        setIsPayPalOpen(false);
        await checkAuth();
      } else {
        toast.error(res.data.message || 'Failed to unlock branch');
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Failed to unlock branch');
    }
  };

  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/branches', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Branch created successfully!');
        queryClient.invalidateQueries('branches');
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create branch');
      }
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/branches/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Branch updated successfully!');
        queryClient.invalidateQueries('branches');
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update branch');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingBranch) {
      updateMutation.mutate({ id: editingBranch.id, data: formData });
    } else {
      if (isLimitReached) {
        toast.error('Branch limit reached! Please purchase an additional branch.');
        return;
      }
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData(branch);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      upiId: ''
    });
    setEditingBranch(null);
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="spinner w-8 h-8"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50 border border-gray-250/60 rounded-2xl p-4.5 mb-6">
        <div>
          <h3 className="text-sm font-extrabold text-slate-800">Branch Locations Status</h3>
          <p className="text-xs text-slate-505 mt-0.5">
            {(user?.role === 'CLIENT' || user?.role === 'ADMIN') ? (
              <span>
                Allocated: <strong className="text-slate-800 font-mono">{branches.length}</strong> / <strong className="text-slate-800 font-mono">{baseLimit === 9999 ? 'Unlimited' : allowedBranchesCount}</strong> branches
                {baseLimit !== 9999 && ` (base: ${baseLimit}, purchased extra: ${extraBranches})`}
              </span>
            ) : (
              <span>Manage all active branch locations.</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isLimitReached && (
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="razorpay">💳 Razorpay</option>
              <option value="paypal">🅿️ PayPal</option>
            </select>
          )}
          {isLimitReached ? (
            <button
              type="button"
              onClick={() => {
                if (paymentMethod === 'paypal') {
                  setIsPayPalOpen(true);
                } else {
                  setIsRazorpayOpen(true);
                }
              }}
              className="px-4 py-2.5 bg-gradient-to-r from-orange-600 to-orange-650 hover:from-amber-500 hover:to-orange-550 text-white font-extrabold rounded-xl text-xs shadow-sm hover:shadow transition duration-200 cursor-pointer flex items-center gap-1.5"
            >
              <span>Unlock Extra Branch ({currencySymbol}{clientPlan?.additionalBranchPrice || 99})</span>
            </button>
          ) : (
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary"
            >
              Add Branch
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {branches.map((branch) => (
          <div key={branch.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{branch.name}</h3>
                <p className="text-sm text-gray-500">Code: {branch.code}</p>
              </div>
              <button
                onClick={() => handleEdit(branch)}
                className="text-blue-600 hover:text-blue-800"
              >
                Edit
              </button>
            </div>
            <div className="space-y-2 text-sm">
              {branch.address && <p className="text-gray-600">{branch.address}</p>}
              {branch.phone && <p className="text-gray-600">Phone: {branch.phone}</p>}
              {branch.email && <p className="text-gray-600">Email: {branch.email}</p>}
              {branch.bankName && (
                <div className="mt-3 pt-3 border-t">
                  <p className="font-medium text-gray-700">Bank Details:</p>
                  <p className="text-gray-600">{branch.bankName}</p>
                  {branch.accountNumber && <p className="text-gray-600">A/C: {branch.accountNumber}</p>}
                  {branch.ifscCode && <p className="text-gray-600">IFSC: {branch.ifscCode}</p>}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Branch Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-lg font-bold">
                {editingBranch ? 'Edit Branch' : 'Add New Branch'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded-lg text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Branch Name *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Branch Code *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Address</label>
                <textarea
                  className="input-field"
                  rows="2"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="input-field"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t pt-3 mt-3">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">Bank Details</h3>
                <div className="space-y-3">
                  <div>
                    <label className="form-label">Bank Name</label>
                    <input
                      type="text"
                      className="input-field"
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="form-label">Account Number</label>
                      <input
                        type="text"
                        className="input-field"
                        value={formData.accountNumber}
                        onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="form-label">IFSC Code</label>
                      <input
                        type="text"
                        className="input-field"
                        value={formData.ifscCode}
                        onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">UPI ID</label>
                    <input
                      type="text"
                      className="input-field"
                      value={formData.upiId}
                      onChange={(e) => setFormData({ ...formData, upiId: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="btn-primary"
                >
                  {(createMutation.isLoading || updateMutation.isLoading) ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    editingBranch ? 'Update Branch' : 'Create Branch'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Razorpay Modal for purchasing additional branch */}
      <RazorpayModal
        isOpen={isRazorpayOpen}
        onClose={() => setIsRazorpayOpen(false)}
        amountInINR={clientPlan?.additionalBranchPrice || 99}
        description={`Purchase additional branch for ${user?.shopName || 'your store'}`}
        clientName={(user?.firstName || '') + ' ' + (user?.lastName || '')}
        clientEmail={user?.email || ''}
        clientPhone={user?.phone || ''}
        onSuccess={handlePurchaseSuccess}
      />

      <PayPalModal
        isOpen={isPayPalOpen}
        onClose={() => setIsPayPalOpen(false)}
        amountInINR={clientPlan?.additionalBranchPrice || 99}
        description={`Purchase additional branch for ${user?.shopName || 'your store'}`}
        onSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

// Technician Management Component
const TechnicianManagement = () => {
  const queryClient = useQueryClient();
  const { selectedBranch } = useBranch();
  const [showModal, setShowModal] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    specialization: ''
  });

  const { data: techniciansData, isLoading } = useQuery(
    ['technicians', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/technicians', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch
    }
  );

  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/technicians', {
        ...data,
        branchId: selectedBranch?.id
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Technician created successfully!');
        queryClient.invalidateQueries('technicians');
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create technician');
      }
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/technicians/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Technician updated successfully!');
        queryClient.invalidateQueries('technicians');
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update technician');
      }
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/api/technicians/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Technician deleted successfully!');
        queryClient.invalidateQueries('technicians');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete technician');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingTechnician) {
      updateMutation.mutate({ id: editingTechnician.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (technician) => {
    setEditingTechnician(technician);
    setFormData({
      name: technician.name,
      phone: technician.phone,
      email: technician.email || '',
      specialization: technician.specialization || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this technician?')) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      specialization: ''
    });
    setEditingTechnician(null);
  };

  const technicians = techniciansData || [];

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="spinner w-8 h-8"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          Manage technicians for {selectedBranch?.name}
        </p>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary"
        >
          Add Technician
        </button>
      </div>

      {technicians.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No technicians found. Add your first technician!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {technicians.map((technician) => (
            <div key={technician.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{technician.name}</h3>
                  {technician.specialization && (
                    <p className="text-sm text-blue-600">{technician.specialization}</p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(technician)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(technician.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <p className="text-gray-600 flex items-center">
                  <span className="font-medium mr-2">Phone:</span>
                  {technician.phone}
                </p>
                {technician.email && (
                  <p className="text-gray-600 flex items-center">
                    <span className="font-medium mr-2">Email:</span>
                    {technician.email}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Technician Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-lg font-bold">
                {editingTechnician ? 'Edit Technician' : 'Add New Technician'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded-lg text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div>
                <label className="form-label">Technician Name *</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter technician name"
                  required
                />
              </div>

              <div>
                <label className="form-label">Contact *</label>
                <input
                  type="tel"
                  className="input-field"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                  required
                />
              </div>

              <div>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="form-label">Specialization</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  placeholder="e.g., Mobile Repair, Laptop Repair"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="btn-primary"
                >
                  {(createMutation.isLoading || updateMutation.isLoading) ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    editingTechnician ? 'Update Technician' : 'Create Technician'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Role Management & RBAC Component
const RoleManagement = () => {
  const queryClient = useQueryClient();
  const { checkAuth } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const MODULES_LIST = [
    { id: 'Dashboard', name: 'Dashboard Analytics', actions: ['view'] },
    { id: 'Jobs', name: 'Repair Jobs Management', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'Customers', name: 'Customer Directory', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'Products', name: 'Product Master Catalog', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'Inventory', name: 'Inventory & Purchases', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'Expenses', name: 'Shop Expense Tracking', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'Statement', name: 'Financial Ledger Statement', actions: ['view'] },
    { id: 'Reports', name: 'Analytics & Performance Reports', actions: ['view'] },
    { id: 'Settings', name: 'System Settings & Preferences', actions: ['view', 'edit'] },
    { id: 'Users', name: 'User & Employee Accounts', actions: ['view', 'create', 'edit', 'delete'] },
  ];

  const getDefaultPermissions = () => {
    const initial = {};
    MODULES_LIST.forEach(mod => {
      initial[mod.id] = {
        view: true,
        create: mod.actions.includes('create'),
        edit: mod.actions.includes('edit'),
        delete: false
      };
    });
    return initial;
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: getDefaultPermissions()
  });

  const { data: rolesData, isLoading: rolesLoading } = useQuery('roles', async () => {
    const response = await api.get('/api/roles');
    return response.data.data;
  });

  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/roles', data);
      return response.data;
    },
    {
      onSuccess: async () => {
        toast.success('Role created successfully!');
        queryClient.invalidateQueries('roles');
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries('roles-for-users');
        setShowModal(false);
        resetForm();
        await checkAuth();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create role');
      }
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/roles/${id}`, data);
      return response.data;
    },
    {
      onSuccess: async () => {
        toast.success('Role updated successfully!');
        queryClient.invalidateQueries('roles');
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries('roles-for-users');
        setShowModal(false);
        resetForm();
        await checkAuth();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update role');
      }
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/api/roles/${id}`);
      return response.data;
    },
    {
      onSuccess: async () => {
        toast.success('Role deleted successfully!');
        queryClient.invalidateQueries('roles');
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries('roles-for-users');
        await checkAuth();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete role');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || getDefaultPermissions()
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this role? Active users assigned to this role must be reassigned first.')) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: getDefaultPermissions()
    });
    setEditingRole(null);
  };

  const handlePermissionToggle = (modId, action) => {
    setFormData(prev => {
      const modPerms = prev.permissions[modId] || { view: false, create: false, edit: false, delete: false };
      const updatedModPerms = { ...modPerms, [action]: !modPerms[action] };

      if (action === 'view' && !updatedModPerms.view) {
        updatedModPerms.create = false;
        updatedModPerms.edit = false;
        updatedModPerms.delete = false;
      }
      if ((action === 'create' || action === 'edit' || action === 'delete') && updatedModPerms[action]) {
        updatedModPerms.view = true;
      }

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [modId]: updatedModPerms
        }
      };
    });
  };

  const handleQuickSelect = (type) => {
    setFormData(prev => {
      const newPerms = {};
      MODULES_LIST.forEach(mod => {
        if (type === 'ALL') {
          newPerms[mod.id] = {
            view: true,
            create: mod.actions.includes('create'),
            edit: mod.actions.includes('edit'),
            delete: mod.actions.includes('delete')
          };
        } else if (type === 'READONLY') {
          newPerms[mod.id] = {
            view: true,
            create: false,
            edit: false,
            delete: false
          };
        } else {
          newPerms[mod.id] = {
            view: false,
            create: false,
            edit: false,
            delete: false
          };
        }
      });
      return { ...prev, permissions: newPerms };
    });
  };

  const roles = rolesData || [];

  if (rolesLoading) {
    return <div className="flex justify-center py-12"><div className="spinner w-8 h-8"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50 border border-slate-200/80 rounded-2xl p-5">
        <div>
          <h3 className="text-base font-extrabold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            Role Settings & Role-Based Access Control (RBAC)
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Create customized employee roles and set granular module permissions (View, Create, Edit, Delete). Next, when creating or updating users, select an assigned role to enforce these access privileges.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center space-x-1.5 shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Create New Role</span>
        </button>
      </div>

      {roles.length === 0 ? (
        <div className="card text-center py-12">
          <Shield className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 font-semibold">No custom roles defined yet.</p>
          <p className="text-xs text-slate-400 mt-1">Click "Create New Role" to configure employee permissions.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {roles.map((role) => {
            const perms = role.permissions || {};
            const allowedModules = Object.keys(perms).filter(m => perms[m]?.view);
            const isBuiltInAdmin = role.name.toUpperCase() === 'ADMIN';

            return (
              <div key={role.id} className="card hover:shadow-md transition duration-200 flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2.5">
                      <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0">
                        <ShieldCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-slate-900 leading-tight">{role.name}</h4>
                        <span className="text-[11px] font-semibold text-slate-400">
                          {role.userCount !== undefined ? `${role.userCount} User(s) Assigned` : 'Custom Role'}
                        </span>
                      </div>
                    </div>
                    {isBuiltInAdmin && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                        System Admin
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-slate-600 mt-3 line-clamp-2 min-h-[32px]">
                    {role.description || 'No description provided.'}
                  </p>

                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                      Accessible Modules ({allowedModules.length}/{MODULES_LIST.length})
                    </span>
                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                      {MODULES_LIST.map(mod => {
                        const p = perms[mod.id];
                        if (!p || !p.view) return null;
                        const actionsText = [
                          p.create && 'C',
                          p.edit && 'E',
                          p.delete && 'D'
                        ].filter(Boolean).join('');

                        return (
                          <span
                            key={mod.id}
                            className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200"
                          >
                            {mod.id} {actionsText ? <span className="ml-1 text-[9px] text-blue-600 font-bold">({actionsText})</span> : ''}
                          </span>
                        );
                      })}
                      {allowedModules.length === 0 && (
                        <span className="text-xs text-red-500 font-medium italic">No module permissions granted</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => handleEdit(role)}
                    className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-bold transition flex items-center space-x-1"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    <span>Edit Permissions</span>
                  </button>
                  {!isBuiltInAdmin && (
                    <button
                      onClick={() => handleDelete(role.id)}
                      className="px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-xs font-bold transition flex items-center space-x-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Delete</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Role Creation / Editing Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-slate-900 to-blue-900 text-white px-6 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center space-x-3">
                <ShieldCheck className="w-6 h-6 text-blue-400" />
                <div>
                  <h2 className="text-lg font-bold">
                    {editingRole ? `Edit Role: ${editingRole.name}` : 'Create New Role'}
                  </h2>
                  <p className="text-xs text-slate-300">Set exact module access and CRUD permissions for employees</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-slate-300 hover:text-white p-1 rounded-lg text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-6 flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Role Name *</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. Senior Technician, Billing Executive, Store Manager"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Brief summary of duties and authority..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>

              {/* Permission Matrix Grid */}
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 pb-3">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Module Access & Action Permissions</h3>
                    <p className="text-xs text-slate-500">Toggle View, Create, Edit, and Delete access for each feature</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleQuickSelect('ALL')}
                      className="px-2.5 py-1 text-[11px] font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md border border-blue-200"
                    >
                      Grant All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickSelect('READONLY')}
                      className="px-2.5 py-1 text-[11px] font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-md border border-slate-300"
                    >
                      View Only
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickSelect('NONE')}
                      className="px-2.5 py-1 text-[11px] font-bold bg-red-50 text-red-700 hover:bg-red-100 rounded-md border border-red-200"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="min-w-full divide-y divide-slate-200 text-xs">
                    <thead className="bg-slate-50 font-bold text-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left">Module / Feature Page</th>
                        <th className="px-4 py-3 text-center">View Access</th>
                        <th className="px-4 py-3 text-center">Create</th>
                        <th className="px-4 py-3 text-center">Edit</th>
                        <th className="px-4 py-3 text-center">Delete</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {MODULES_LIST.map((mod) => {
                        const p = formData.permissions[mod.id] || { view: false, create: false, edit: false, delete: false };
                        const hasCreate = mod.actions.includes('create');
                        const hasEdit = mod.actions.includes('edit');
                        const hasDelete = mod.actions.includes('delete');

                        return (
                          <tr key={mod.id} className="hover:bg-slate-50/80 transition">
                            <td className="px-4 py-3 font-semibold text-slate-800">
                              {mod.name}
                            </td>
                            {/* View Checkbox */}
                            <td className="px-4 py-3 text-center">
                              <label className="inline-flex items-center justify-center cursor-pointer p-1">
                                <input
                                  type="checkbox"
                                  checked={Boolean(p.view)}
                                  onChange={() => handlePermissionToggle(mod.id, 'view')}
                                  className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
                                />
                              </label>
                            </td>
                            {/* Create Checkbox */}
                            <td className="px-4 py-3 text-center">
                              {hasCreate ? (
                                <label className="inline-flex items-center justify-center cursor-pointer p-1">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(p.create)}
                                    onChange={() => handlePermissionToggle(mod.id, 'create')}
                                    className="w-4 h-4 text-green-600 rounded border-slate-300 focus:ring-green-500 cursor-pointer"
                                  />
                                </label>
                              ) : (
                                <span className="text-slate-300 font-mono text-[10px]">—</span>
                              )}
                            </td>
                            {/* Edit Checkbox */}
                            <td className="px-4 py-3 text-center">
                              {hasEdit ? (
                                <label className="inline-flex items-center justify-center cursor-pointer p-1">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(p.edit)}
                                    onChange={() => handlePermissionToggle(mod.id, 'edit')}
                                    className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500 cursor-pointer"
                                  />
                                </label>
                              ) : (
                                <span className="text-slate-300 font-mono text-[10px]">—</span>
                              )}
                            </td>
                            {/* Delete Checkbox */}
                            <td className="px-4 py-3 text-center">
                              {hasDelete ? (
                                <label className="inline-flex items-center justify-center cursor-pointer p-1">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(p.delete)}
                                    onChange={() => handlePermissionToggle(mod.id, 'delete')}
                                    className="w-4 h-4 text-red-600 rounded border-slate-300 focus:ring-red-500 cursor-pointer"
                                  />
                                </label>
                              ) : (
                                <span className="text-slate-300 font-mono text-[10px]">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="btn-primary inline-flex items-center space-x-1.5"
                >
                  {(createMutation.isLoading || updateMutation.isLoading) ? (
                    <>
                      <div className="spinner w-4 h-4 border-white mr-1" />
                      <span>Saving Role...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save Role Settings</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// User Management Component
const UserManagement = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'EMPLOYEE',
    roleId: '',
    branchId: ''
  });

  const { data: usersData, isLoading: usersLoading } = useQuery('users', async () => {
    const response = await api.get('/api/users');
    return response.data.data;
  });

  const { data: branchesData } = useQuery('branches-for-users', async () => {
    const response = await api.get('/api/branches');
    return response.data.data;
  });

  const { data: rolesData } = useQuery('roles-for-users', async () => {
    const response = await api.get('/api/roles');
    return response.data.data;
  });

  const rolesList = rolesData || [];

  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/users', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('User created successfully!');
        queryClient.invalidateQueries('users');
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create user');
      }
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/users/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('User updated successfully!');
        queryClient.invalidateQueries('users');
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user');
      }
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/api/users/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('User deleted successfully!');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete user');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingUser) {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      updateMutation.mutate({ id: editingUser.id, data: updateData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      roleId: user.roleId || '',
      branchId: user.branchId
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'EMPLOYEE',
      roleId: '',
      branchId: ''
    });
    setEditingUser(null);
  };

  const users = usersData || [];
  const branches = branchesData || [];

  const getRoleBadgeColor = (role) => {
    switch (role?.toUpperCase()) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800';
      case 'MANAGER':
        return 'bg-blue-100 text-blue-800';
      case 'EMPLOYEE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-purple-100 text-purple-800 border border-purple-200';
    }
  };

  if (usersLoading) {
    return <div className="flex justify-center py-12"><div className="spinner w-8 h-8"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          Manage system users and assign defined roles from Role Settings
        </p>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary"
        >
          Add User
        </button>
      </div>

      {users.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No users found. Add your first user!</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-bold rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.branch?.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-lg font-bold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded-lg text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Last Name *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Username *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="form-label">
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  className="input-field"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Leave blank to keep current password' : ''}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Assigned Role *</label>
                  <select
                    className="input-field cursor-pointer"
                    value={formData.roleId || formData.role}
                    onChange={(e) => {
                      const val = e.target.value;
                      const matched = rolesList.find(r => r.id === val || r.name === val);
                      if (matched) {
                        setFormData({ ...formData, roleId: matched.id, role: matched.name });
                      } else {
                        setFormData({ ...formData, roleId: '', role: val });
                      }
                    }}
                    required
                  >
                    <option value="">-- Select Role --</option>
                    {rolesList.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} {r.description ? `(${r.description})` : ''}
                      </option>
                    ))}
                    {rolesList.length === 0 && (
                      <>
                        <option value="EMPLOYEE">Employee</option>
                        <option value="MANAGER">Manager</option>
                        <option value="ADMIN">Admin</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="form-label">Branch *</label>
                  <select
                    className="input-field"
                    value={formData.branchId}
                    onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                    required
                  >
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="btn-primary"
                >
                  {(createMutation.isLoading || updateMutation.isLoading) ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    editingUser ? 'Update User' : 'Create User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Category Management Component
const CategoryManagement = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const { data: categoriesData, isLoading } = useQuery('categories', async () => {
    const response = await api.get('/api/categories');
    return response.data.data;
  });

  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/categories', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Category created successfully!');
        queryClient.invalidateQueries('categories');
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create category');
      }
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/categories/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Category updated successfully!');
        queryClient.invalidateQueries('categories');
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update category');
      }
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/api/categories/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Category deleted successfully!');
        queryClient.invalidateQueries('categories');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete category');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
    setEditingCategory(null);
  };

  const categories = categoriesData || [];

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="spinner w-8 h-8"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          Manage product categories for better organization
        </p>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary"
        >
          Add Category
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="card text-center py-12">
          <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No categories found. Add your first category!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <div key={category.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{category.name}</h3>
                  {category.description && (
                    <p className="text-sm text-gray-600">{category.description}</p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(category)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-500">
                  <Tag className="w-4 h-4 mr-2" />
                  <span>{category._count?.products || 0} product(s)</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-lg font-bold">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded-lg text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div>
                <label className="form-label">Category Name *</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Mobile Parts, Laptop Parts"
                  required
                />
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  className="input-field"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this category"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="btn-primary"
                >
                  {(createMutation.isLoading || updateMutation.isLoading) ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    editingCategory ? 'Update Category' : 'Create Category'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Terms & Conditions Management Component
const TermsManagement = () => {
  const queryClient = useQueryClient();
  const [terms, setTerms] = useState(['']);
  const [jobsheetTerms, setJobsheetTerms] = useState(['']);
  const [accessoryTerms, setAccessoryTerms] = useState(['']);
  const [companyData, setCompanyData] = useState(null);

  const { isLoading } = useQuery('company-settings', async () => {
    const response = await api.get('/api/settings/company');
    return response.data.data;
  }, {
    onSuccess: (data) => {
      if (data) {
        setCompanyData(data);
        if (data.terms) {
          try {
            const parsed = JSON.parse(data.terms);
            if (Array.isArray(parsed)) {
              setTerms(parsed.length > 0 ? parsed : ['']);
            } else {
              setTerms(['']);
            }
          } catch (e) {
            setTerms(['']);
          }
        } else {
          setTerms(['']);
        }

        if (data.jobsheetTerms) {
          try {
            const parsed = JSON.parse(data.jobsheetTerms);
            if (Array.isArray(parsed)) {
              setJobsheetTerms(parsed.length > 0 ? parsed : ['']);
            } else {
              setJobsheetTerms(['']);
            }
          } catch (e) {
            setJobsheetTerms(['']);
          }
        } else {
          setJobsheetTerms(['']);
        }

        if (data.accessoryTerms) {
          try {
            const parsed = JSON.parse(data.accessoryTerms);
            if (Array.isArray(parsed)) {
              setAccessoryTerms(parsed.length > 0 ? parsed : ['']);
            } else {
              setAccessoryTerms(['']);
            }
          } catch (e) {
            setAccessoryTerms(['']);
          }
        } else {
          setAccessoryTerms(['']);
        }
      }
    }
  });

  const saveMutation = useMutation(
    async (data) => {
      const response = await api.put('/api/settings/company', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Terms & Conditions updated successfully!');
        queryClient.invalidateQueries('company-settings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
    }
  );

  const handleAddTerm = () => {
    setTerms([...terms, '']);
  };

  const handleRemoveTerm = (index) => {
    const updated = terms.filter((_, i) => i !== index);
    setTerms(updated.length > 0 ? updated : ['']);
  };

  const handleTermChange = (index, value) => {
    const updated = [...terms];
    updated[index] = value;
    setTerms(updated);
  };

  const handleAddJobsheetTerm = () => {
    setJobsheetTerms([...jobsheetTerms, '']);
  };

  const handleRemoveJobsheetTerm = (index) => {
    const updated = jobsheetTerms.filter((_, i) => i !== index);
    setJobsheetTerms(updated.length > 0 ? updated : ['']);
  };

  const handleJobsheetTermChange = (index, value) => {
    const updated = [...jobsheetTerms];
    updated[index] = value;
    setJobsheetTerms(updated);
  };

  const handleAddAccessoryTerm = () => {
    setAccessoryTerms([...accessoryTerms, '']);
  };

  const handleRemoveAccessoryTerm = (index) => {
    const updated = accessoryTerms.filter((_, i) => i !== index);
    setAccessoryTerms(updated.length > 0 ? updated : ['']);
  };

  const handleAccessoryTermChange = (index, value) => {
    const updated = [...accessoryTerms];
    updated[index] = value;
    setAccessoryTerms(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const filteredTerms = terms.map(t => t.trim()).filter(t => t !== '');
    const filteredJobsheetTerms = jobsheetTerms.map(t => t.trim()).filter(t => t !== '');
    const filteredAccessoryTerms = accessoryTerms.map(t => t.trim()).filter(t => t !== '');
    
    const requestData = {
      ...companyData,
      terms: JSON.stringify(filteredTerms),
      jobsheetTerms: JSON.stringify(filteredJobsheetTerms),
      accessoryTerms: JSON.stringify(filteredAccessoryTerms)
    };

    saveMutation.mutate(requestData);
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="spinner w-8 h-8"></div></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Accessories Bill Terms Card */}
      <div className="card space-y-6 border-2 border-emerald-500/20 bg-gradient-to-b from-emerald-50/30 to-transparent">
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span>
            Accessories Bill Terms & Conditions
          </h3>
          <p className="text-sm text-gray-500">
            Add the terms and conditions that will appear specifically on the printed Accessories / Direct Billing A4 Sheet Bill.
          </p>
        </div>

        <div className="space-y-4">
          {accessoryTerms.map((term, index) => (
            <div key={index} className="flex items-center space-x-3">
              <span className="text-sm font-semibold text-emerald-600 w-8 flex-shrink-0">
                #{index + 1}
              </span>
              <input
                type="text"
                className="input-field flex-1"
                value={term}
                onChange={(e) => handleAccessoryTermChange(index, e.target.value)}
                placeholder="e.g. 1. All products sold carry standard manufacturer warranty where applicable."
                required
              />
              <button
                type="button"
                onClick={() => handleRemoveAccessoryTerm(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded border border-red-200"
                title="Remove Term"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex justify-start">
          <button
            type="button"
            onClick={handleAddAccessoryTerm}
            className="btn-secondary inline-flex items-center text-emerald-700 hover:text-emerald-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add More Accessories Bill Terms
          </button>
        </div>
      </div>

      {/* Invoice Terms Card */}
      <div className="card space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Invoice Terms & Conditions</h3>
          <p className="text-sm text-gray-500">
            Add the terms and conditions that will appear specifically on repair invoices and general payment receipts.
          </p>
        </div>

        <div className="space-y-4">
          {terms.map((term, index) => (
            <div key={index} className="flex items-center space-x-3">
              <span className="text-sm font-semibold text-gray-400 w-8 flex-shrink-0">
                #{index + 1}
              </span>
              <input
                type="text"
                className="input-field flex-1"
                value={term}
                onChange={(e) => handleTermChange(index, e.target.value)}
                placeholder="e.g. No refund after delivery, 3 months service warranty, etc."
                required
              />
              <button
                type="button"
                onClick={() => handleRemoveTerm(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded border border-red-200"
                title="Remove Term"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex justify-start">
          <button
            type="button"
            onClick={handleAddTerm}
            className="btn-secondary inline-flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add More Invoice Terms
          </button>
        </div>
      </div>

      {/* Jobsheet Terms Card */}
      <div className="card space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Jobsheet Terms & Conditions</h3>
          <p className="text-sm text-gray-500">
            Add the terms and conditions that will appear specifically on the printed Job Sheets/Estimate Sheets.
          </p>
        </div>

        <div className="space-y-4">
          {jobsheetTerms.map((term, index) => (
            <div key={index} className="flex items-center space-x-3">
              <span className="text-sm font-semibold text-gray-400 w-8 flex-shrink-0">
                #{index + 1}
              </span>
              <input
                type="text"
                className="input-field flex-1"
                value={term}
                onChange={(e) => handleJobsheetTermChange(index, e.target.value)}
                placeholder="e.g. Please bring this job sheet for delivery, parts warranty as per manufacturer, etc."
                required
              />
              <button
                type="button"
                onClick={() => handleRemoveJobsheetTerm(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded border border-red-200"
                title="Remove Term"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex justify-start">
          <button
            type="button"
            onClick={handleAddJobsheetTerm}
            className="btn-secondary inline-flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add More Jobsheet Terms
          </button>
        </div>
      </div>

      {/* Action Footer */}
      <div className="flex justify-end p-4 bg-gray-50 rounded-xl border border-gray-250/60">
        <button
          type="submit"
          disabled={saveMutation.isLoading}
          className="btn-primary inline-flex items-center"
        >
          {saveMutation.isLoading ? (
            <>
              <div className="spinner mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save All Terms & Conditions
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Receipt Settings Component
const ReceiptSettings = () => {
  const queryClient = useQueryClient();
  const [companyData, setCompanyData] = useState(null);
  const [settings, setSettings] = useState({
    showAt: true,
    labelAt: 'AT',
    showRepairItem: true,
    labelRepairItem: 'REPAIR ITEM',
    showDefect: true,
    labelDefect: 'DEFECT',
    showRepairDetails: true,
    labelRepairDetails: 'REPAIR DETAILS',
    showRepairPrice: true,
    labelRepairPrice: 'REPAIR PRICE',
    showTotalAmount: true,
    labelTotalAmount: 'TOTAL AMOUNT',
    showTotalPaid: true,
    labelTotalPaid: 'TOTAL PAID / ADVANCE',
    showBalanceDue: true,
    labelBalanceDue: 'BALANCE DUE',
    showTerms: true,
    labelTerms: 'TERMS AND CONDITIONS*',
    showSignRecipient: true,
    labelSignRecipient: 'SIGN RECIPIENT',
    showSignClient: true,
    labelSignClient: 'SIGN CLIENT',
    showCustomerSignature: true,
    labelCustomerSignatureText: 'CUSTOMER SIGNATURE',
    showFooterMessage: true,
    footerMessage: 'Thank you for choosing My Company!',
    showDate: true,
    labelDate: 'DATE',
    showName: true,
    labelName: 'NAME',
    showPhone: true,
    labelPhone: 'PHONE',
    showBarcode: true,
    labelBarcode: 'BAR CODE MANAGEMENT',
    showAddress: true,
    labelAddress: 'Main Branch Address',
    logoSize: 'large',
    showStoreNameUnderLogo: true
  });

  const { isLoading } = useQuery('company-settings', async () => {
    const response = await api.get('/api/settings/company');
    return response.data.data;
  }, {
    onSuccess: (data) => {
      if (data) {
        setCompanyData(data);
        if (data.receiptSettings) {
          try {
            const parsed = JSON.parse(data.receiptSettings);
            setSettings(prev => ({ ...prev, ...parsed }));
          } catch (e) {
            console.error('Failed to parse receipt settings:', e);
          }
        }
      }
    }
  });

  const saveMutation = useMutation(
    async (data) => {
      const response = await api.put('/api/settings/company', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Receipt settings updated successfully!');
        queryClient.invalidateQueries('company-settings');
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update settings';
        toast.error(errorMsg);
      }
    }
  );

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const requestData = {
      ...companyData,
      receiptSettings: JSON.stringify(settings)
    };
    saveMutation.mutate(requestData);
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="spinner w-8 h-8"></div></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="card space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Receipt & PDF Settings</h3>
        <p className="text-sm text-gray-500">
          Customize content and visibility of fields on the printed/PDF receipts.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Visibility Toggles */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 border-b pb-2">Header & Customer Fields</h4>
          
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showDate}
                onChange={() => handleToggle('showDate')}
              />
              <span className="text-sm text-gray-700">Show Date</span>
            </label>
            {settings.showDate && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for Date"
                  value={settings.labelDate || ''}
                  onChange={(e) => handleChange('labelDate', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showName}
                onChange={() => handleToggle('showName')}
              />
              <span className="text-sm text-gray-700">Show Customer Name</span>
            </label>
            {settings.showName && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for Customer Name"
                  value={settings.labelName || ''}
                  onChange={(e) => handleChange('labelName', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showPhone}
                onChange={() => handleToggle('showPhone')}
              />
              <span className="text-sm text-gray-700">Show Customer Phone</span>
            </label>
            {settings.showPhone && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for Customer Phone"
                  value={settings.labelPhone || ''}
                  onChange={(e) => handleChange('labelPhone', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showBarcode}
                onChange={() => handleToggle('showBarcode')}
              />
              <span className="text-sm text-gray-700">Show Barcode & QR Code</span>
            </label>
            {settings.showBarcode && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for Barcode Management"
                  value={settings.labelBarcode || ''}
                  onChange={(e) => handleChange('labelBarcode', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showAddress}
                onChange={() => handleToggle('showAddress')}
              />
              <span className="text-sm text-gray-700">Show Main Branch Address</span>
            </label>
            {settings.showAddress && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for Branch Address"
                  value={settings.labelAddress || ''}
                  onChange={(e) => handleChange('labelAddress', e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 border-b pb-2">Repair Items Table Columns</h4>
          
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showAt}
                onChange={() => handleToggle('showAt')}
              />
              <span className="text-sm text-gray-700">Show "At" (Index Column)</span>
            </label>
            {settings.showAt && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'At'"
                  value={settings.labelAt || ''}
                  onChange={(e) => handleChange('labelAt', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showRepairItem}
                onChange={() => handleToggle('showRepairItem')}
              />
              <span className="text-sm text-gray-700">Show "Repair Item" Column</span>
            </label>
            {settings.showRepairItem && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'Repair Item'"
                  value={settings.labelRepairItem || ''}
                  onChange={(e) => handleChange('labelRepairItem', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showDefect}
                onChange={() => handleToggle('showDefect')}
              />
              <span className="text-sm text-gray-700">Show "Defect" Column</span>
            </label>
            {settings.showDefect && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'Defect'"
                  value={settings.labelDefect || ''}
                  onChange={(e) => handleChange('labelDefect', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showRepairDetails}
                onChange={() => handleToggle('showRepairDetails')}
              />
              <span className="text-sm text-gray-700">Show "Repair Details" Column</span>
            </label>
            {settings.showRepairDetails && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'Repair Details'"
                  value={settings.labelRepairDetails || ''}
                  onChange={(e) => handleChange('labelRepairDetails', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showRepairPrice}
                onChange={() => handleToggle('showRepairPrice')}
              />
              <span className="text-sm text-gray-700">Show "Repair Price" Column</span>
            </label>
            {settings.showRepairPrice && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'Repair Price'"
                  value={settings.labelRepairPrice || ''}
                  onChange={(e) => handleChange('labelRepairPrice', e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 border-b pb-2">Totals & Signatures</h4>
          
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showTotalAmount}
                onChange={() => handleToggle('showTotalAmount')}
              />
              <span className="text-sm text-gray-700">Show Total Amount</span>
            </label>
            {settings.showTotalAmount && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'Total Amount'"
                  value={settings.labelTotalAmount || ''}
                  onChange={(e) => handleChange('labelTotalAmount', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showTotalPaid}
                onChange={() => handleToggle('showTotalPaid')}
              />
              <span className="text-sm text-gray-700">Show Total Paid / Advance</span>
            </label>
            {settings.showTotalPaid && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'Total Paid / Advance'"
                  value={settings.labelTotalPaid || ''}
                  onChange={(e) => handleChange('labelTotalPaid', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showBalanceDue}
                onChange={() => handleToggle('showBalanceDue')}
              />
              <span className="text-sm text-gray-700">Show Balance Due</span>
            </label>
            {settings.showBalanceDue && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'Balance Due'"
                  value={settings.labelBalanceDue || ''}
                  onChange={(e) => handleChange('labelBalanceDue', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showTerms}
                onChange={() => handleToggle('showTerms')}
              />
              <span className="text-sm text-gray-700">Show Terms & Conditions</span>
            </label>
            {settings.showTerms && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for 'Terms & Conditions'"
                  value={settings.labelTerms || ''}
                  onChange={(e) => handleChange('labelTerms', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showSignRecipient}
                onChange={() => handleToggle('showSignRecipient')}
              />
              <span className="text-sm text-gray-700">Show Sign Recipient Box</span>
            </label>
            {settings.showSignRecipient && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for Recipient Sign"
                  value={settings.labelSignRecipient || ''}
                  onChange={(e) => handleChange('labelSignRecipient', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showSignClient}
                onChange={() => handleToggle('showSignClient')}
              />
              <span className="text-sm text-gray-700">Show Sign Client Box</span>
            </label>
            {settings.showSignClient && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for Client Sign"
                  value={settings.labelSignClient || ''}
                  onChange={(e) => handleChange('labelSignClient', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                checked={settings.showCustomerSignature}
                onChange={() => handleToggle('showCustomerSignature')}
              />
              <span className="text-sm text-gray-700">Show Customer Signature Text</span>
            </label>
            {settings.showCustomerSignature && (
              <div className="pl-7">
                <input
                  type="text"
                  className="input-field py-1 px-2 text-xs"
                  placeholder="Custom label for Customer Signature"
                  value={settings.labelCustomerSignatureText || ''}
                  onChange={(e) => handleChange('labelCustomerSignatureText', e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 border-b pb-2">Branding & Footer</h4>
          
          <div>
            <label className="form-label">Logo Display Size</label>
            <select
              className="input-field"
              value={settings.logoSize}
              onChange={(e) => handleChange('logoSize', e.target.value)}
            >
              <option value="normal">Normal Size</option>
              <option value="large">Large Size</option>
            </select>
          </div>

          <label className="flex items-center space-x-3 cursor-pointer pt-2">
            <input
              type="checkbox"
              className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
              checked={settings.showStoreNameUnderLogo}
              onChange={() => handleToggle('showStoreNameUnderLogo')}
            />
            <span className="text-sm text-gray-700">Show Store Name Below Logo</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer pt-2">
            <input
              type="checkbox"
              className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
              checked={settings.showFooterMessage}
              onChange={() => handleToggle('showFooterMessage')}
            />
            <span className="text-sm text-gray-700">Show Footer Thank You Message</span>
          </label>

          {settings.showFooterMessage && (
            <div>
              <label className="form-label">Thank You Message</label>
              <input
                type="text"
                className="input-field"
                value={settings.footerMessage}
                onChange={(e) => handleChange('footerMessage', e.target.value)}
                placeholder="Thank you for choosing My Company!"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t">
        <button
          type="submit"
          disabled={saveMutation.isLoading}
          className="btn-primary inline-flex items-center"
        >
          {saveMutation.isLoading ? (
            <>
              <div className="spinner mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Receipt Settings
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default Settings;


